import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
	type FC,
	type KeyboardEvent,
	type MouseEvent,
	memo,
} from "react";
import { audioEngine } from "$/modules/audio/audio-engine.ts";
import type { WordSegment } from "$/modules/segmentation/utils/segment-processing.ts";
import { displayRomanizationInSyncAtom } from "$/modules/settings/states/index.ts";
import {
	selectedWordIdAtom,
	selectionPanOffsetAtom,
	timelineDragAtom,
} from "$/modules/spectrogram/states/dnd.ts";
import { spectrogramLyricMaxCharsAtom } from "$/modules/spectrogram/states/index.ts";
import {
	editingTimeFieldAtom,
	selectedLinesAtom,
	selectedWordsAtom,
} from "$/states/main.ts";
import styles from "./LyricWordSegment.module.css";

interface LyricWordSegmentProps {
	lineId: string;
	segment: WordSegment;
	lineStartTime: number;
	zoom: number;
}

export const LyricWordSegment: FC<LyricWordSegmentProps> = memo(({
	lineId,
	segment,
	lineStartTime,
	zoom,
}) => {
	const [selectedWordId, setSelectedWordId] = useAtom(selectedWordIdAtom);
	const setTimelineDrag = useSetAtom(timelineDragAtom);
	const setSelectedWords = useSetAtom(selectedWordsAtom);
	const setSelectedLines = useSetAtom(selectedLinesAtom);
	const showPerWordRomanization = useAtomValue(displayRomanizationInSyncAtom);
	const editingTimeField = useAtomValue(editingTimeFieldAtom);
	const selectionPanOffset = useAtomValue(selectionPanOffsetAtom);
	const selectedWords = useAtomValue(selectedWordsAtom);
	const selectedLines = useAtomValue(selectedLinesAtom);

	const lyricMaxChars = useAtomValue(spectrogramLyricMaxCharsAtom);
	const { startTime, endTime, word, romanWord } = segment;
	const displayWord = lyricMaxChars > 0 && word.length > lyricMaxChars
		? word.slice(0, lyricMaxChars) + "\u2026"
		: word;

	if (startTime == null || endTime == null || endTime <= startTime) {
		return null;
	}

	const left = ((startTime - lineStartTime) / 1000) * zoom;
	const width = ((endTime - startTime) / 1000) * zoom;

	const isSelected = selectedWordId === segment.id || selectedWords.has(segment.id);

	const selectGlobal = (e?: MouseEvent | KeyboardEvent) => {
		const isShift = e?.shiftKey;
		setSelectedWordId(segment.id);

		if (isShift) {
			setSelectedWords((prev) => {
				const newSet = new Set(prev);
				newSet.add(segment.id);
				return newSet;
			});
			setSelectedLines((prev) => {
				const newSet = new Set(prev);
				newSet.add(lineId);
				return newSet;
			});
		} else {
			setSelectedWords(new Set([segment.id]));
			setSelectedLines(new Set([lineId]));
		}
	};

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		e.stopPropagation();
		selectGlobal(e);
	};

	const handlePanStart = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		if (e.button !== 0) return;

		e.preventDefault();
		e.stopPropagation();

		const isCurrentlySelected = selectedWords.has(segment.id) || selectedLines.has(lineId);

		// If dragging an already selected word, initiate a selection-pan
		if (isCurrentlySelected) {
			setTimelineDrag({
				type: "selection-pan",
				startX: e.clientX,
			});
		} else {
			// Otherwise just select this one and word-pan
			selectGlobal(e);
			setTimelineDrag({
				type: "word-pan",
				lineId: lineId,
				wordId: segment.id,
				startX: e.clientX,
				initialWordStartMS: segment.startTime,
			});
		}
	};

	const handleContextMenu = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		e.preventDefault();
		e.stopPropagation();
		selectGlobal(e);
		if (startTime != null && endTime != null) {
			audioEngine.auditionRange(startTime / 1000, endTime / 1000);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			selectGlobal(e);
			if (startTime != null && endTime != null) {
				audioEngine.auditionRange(startTime / 1000, endTime / 1000);
			}
		}
	};

	const timelineDragAtomValue = useAtomValue(timelineDragAtom);
	const isSelectedByPan = isSelected && timelineDragAtomValue?.type === "selection-pan";
	const effectiveLeft = isSelectedByPan ? left + (selectionPanOffset * zoom) / 1000 : left;

	const dynamicStyles = {
		left: `${effectiveLeft}px`,
		width: `${width}px`,
		backgroundColor: isSelected ? "var(--accent-a6)" : "transparent",
	};

	const hasRoman =
		showPerWordRomanization && romanWord && romanWord.trim() !== "";

	return (
		// biome-ignore lint/a11y/useSemanticElements: 在这里用 <button> 显然不正确
		<div
			className={styles.wordSegment}
			style={dynamicStyles}
			onClick={handleClick}
			onMouseDown={handlePanStart}
			onContextMenu={handleContextMenu}
			role="button"
			tabIndex={0}
			onKeyDown={handleKeyDown}
			data-word-id={segment.id}
			data-parent-line-id={lineId}
		>
			{hasRoman ? (
				<>
					<span className={styles.romanText}>{romanWord}</span>
					<span className={styles.originText}>{displayWord}</span>
				</>
			) : (
				displayWord
			)}
		</div>
	);
});
